/**
 * Program 12.1 UX4 — AI Bid Brief signals (deterministic + advisory).
 */
import type { BidWorkspaceViewModel } from './bidWorkspaceModel';
import { formatMoney } from '@/utils/format';

export type BidBriefSignalKind = 'FACT' | 'CALCULATION' | 'MISSING' | 'INFERENCE';

export type BidBriefSignal = {
  id: string;
  kind: BidBriefSignalKind;
  label: string;
  detail?: string;
};

export function buildBidBriefSignals(input: {
  vm: BidWorkspaceViewModel;
  proposal?: Record<string, unknown> | null;
}): BidBriefSignal[] {
  const signals: BidBriefSignal[] = [];
  const { vm, proposal } = input;

  signals.push({
    id: 'deadline',
    kind: 'FACT',
    label: 'Submission deadline',
    detail: vm.submissionDueLabel,
  });

  if (proposal?.commercial_value != null) {
    signals.push({
      id: 'bid-price',
      kind: 'FACT',
      label: 'Bid price',
      detail: formatMoney(Number(proposal.commercial_value), { currency: 'AED' }),
    });
  }

  signals.push({
    id: 'readiness',
    kind: 'CALCULATION',
    label: 'Submission readiness',
    detail: `${vm.overallSubmissionReadiness}%`,
  });

  for (const a of vm.attention.slice(0, 6)) {
    signals.push({
      id: a.id,
      kind: a.severity === 'critical' ? 'INFERENCE' : 'FACT',
      label: a.title,
      detail: a.detail,
    });
  }

  if (vm.isNoBidApproved) {
    signals.push({ id: 'no-bid', kind: 'FACT', label: 'NO-BID approved — submission not applicable' });
  }

  return signals;
}

export function buildBidBriefQuestions(signals: BidBriefSignal[]): string[] {
  const questions: string[] = [];
  if (signals.some((s) => s.label.toLowerCase().includes('addendum'))) {
    questions.push('Has every addendum been fully incorporated?');
  }
  if (signals.some((s) => s.label.toLowerCase().includes('bond'))) {
    questions.push('Is the bid bond received and validated?');
  }
  if (signals.some((s) => s.label.toLowerCase().includes('margin'))) {
    questions.push('Is the current margin acceptable for this risk profile?');
  }
  if (!questions.length) {
    questions.push('Are all mandatory submission documents signed and ready?');
  }
  return questions;
}
